import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, GripVertical, Plus, Trash2 } from "lucide-react";
import "./directoryregister.css";

const generateId = () => Math.random().toString(36).slice(2, 10);

const defaultRegisterData = () => ({
  propertyName: "",
  floors: [],
  /** 1–4 fixed columns, or omit / use Auto in UI for legacy floor-based column count */
  columnCount: 2,
  titleSize: 22,
  floorSize: 16,
  rowSize: 13,
  padding: 8,
  cornerRadius: 0,
  backgroundColor: "transparent",
  textColor: "#000000",
});

function draftColumnCountFromSource(src) {
  if (!src || typeof src !== "object") return {};
  const raw = src.columnCount;
  if (raw === undefined || raw === null || raw === "") return {};
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < 1 || n > 4) return {};
  return { columnCount: n };
}

/** Deep-copy floors/rows from canvas-linked state into panel-local draft (all floors, not a subset). */
function registerDataToLocalDraft(src) {
  if (!src || typeof src !== "object") return defaultRegisterData();
  const floors = Array.isArray(src.floors) ? src.floors : [];
  return {
    propertyName: src.propertyName ?? "",
    floors: floors.map((f) => ({
      ...f,
      rows: Array.isArray(f.rows)
        ? f.rows.map((r) => ({ ...r }))
        : [],
    })),
    ...draftColumnCountFromSource(src),
    titleSize: src.titleSize ?? 22,
    floorSize: src.floorSize ?? 16,
    rowSize: src.rowSize ?? 13,
    padding: src.padding ?? 8,
    cornerRadius: src.cornerRadius ?? 0,
    backgroundColor: src.backgroundColor ?? "transparent",
    textColor: src.textColor ?? "#000000",
    ...(typeof src._layoutW === "number" && src._layoutW > 0
      ? { _layoutW: src._layoutW }
      : {}),
    ...(typeof src._layoutH === "number" && src._layoutH > 0
      ? { _layoutH: src._layoutH }
      : {}),
  };
}

function DirectoryRegister({
  registerData,
  onRegisterDataChange,
  onAddToCanvas,
  isEditing,
  /** When user selects the register on the canvas, collapse all floor rows for easier scanning. */
  editingRegisterObjectId,
}) {
  const [expandedFloors, setExpandedFloors] = useState({});
  const [localData, setLocalData] = useState(defaultRegisterData);
  const [newFloorName, setNewFloorName] = useState("");
  const [floorNameError, setFloorNameError] = useState("");
  const floorsListRef = useRef(null);
  const lastAddedFloorIdRef = useRef(null);
  const prevEditingRegisterIdRef = useRef(undefined);

  const isControlled = registerData != null;
  // Keep local draft in sync while editing on canvas so after remove/re-add we still have every floor.
  useEffect(() => {
    if (registerData != null) {
      setLocalData(registerDataToLocalDraft(registerData));
    }
  }, [registerData]);

  useEffect(() => {
    if (
      editingRegisterObjectId != null &&
      editingRegisterObjectId !== prevEditingRegisterIdRef.current
    ) {
      setExpandedFloors({});
    }
    prevEditingRegisterIdRef.current = editingRegisterObjectId;
  }, [editingRegisterObjectId]);

  const data = isControlled ? registerData : localData;

  const updateData = (next) => {
    if (isControlled) onRegisterDataChange?.(next);
    else setLocalData(next);
  };

  const setPropertyName = (value) => {
    updateData({ ...data, propertyName: value });
  };

  const setColumnCountMode = (value) => {
    if (value === "" || value === "auto") {
      const next = { ...data };
      delete next.columnCount;
      updateData(next);
      return;
    }
    const n = Math.round(Number(value));
    if (!Number.isFinite(n) || n < 1 || n > 4) return;
    updateData({ ...data, columnCount: n });
  };

  const columnSelectValue =
    data.columnCount !== undefined &&
    data.columnCount !== null &&
    data.columnCount !== "" &&
    Number.isFinite(Number(data.columnCount)) &&
    Number(data.columnCount) >= 1 &&
    Number(data.columnCount) <= 4
      ? String(Math.round(Number(data.columnCount)))
      : "auto";

  const addFloor = () => {
    const cleanName = newFloorName.trim();
    if (!cleanName) {
      setFloorNameError("Floor name is required.");
      return;
    }
    const newFloor = {
      id: generateId(),
      name: cleanName,
      rows: [],
    };
    lastAddedFloorIdRef.current = newFloor.id;
    updateData({
      ...data,
      floors: [...(data.floors || []), newFloor],
    });
    setNewFloorName("");
    setFloorNameError("");
  };

  useEffect(() => {
    const id = lastAddedFloorIdRef.current;
    if (!id || !floorsListRef.current) return;
    const run = () => {
      const root = floorsListRef.current;
      if (!root) return;
      const el = root.querySelector(`[data-floor-id="${CSS.escape(String(id))}"]`);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        lastAddedFloorIdRef.current = null;
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, [data.floors]);

  const updateFloor = (floorId, updates) => {
    updateData({
      ...data,
      floors: (data.floors || []).map((f) =>
        f.id === floorId ? { ...f, ...updates } : f
      ),
    });
  };

  const removeFloor = (floorId) => {
    updateData({
      ...data,
      floors: (data.floors || []).filter((f) => f.id !== floorId),
    });
    setExpandedFloors((prev) => {
      const next = { ...prev };
      delete next[floorId];
      return next;
    });
  };

  const addRow = (floorId) => {
    updateData({
      ...data,
      floors: (data.floors || []).map((f) =>
        f.id === floorId
          ? { ...f, rows: [...(f.rows || []), { id: generateId(), text: "" }] }
          : f
      ),
    });
  };

  const updateRow = (floorId, rowId, text) => {
    updateData({
      ...data,
      floors: (data.floors || []).map((f) =>
        f.id === floorId
          ? {
              ...f,
              rows: (f.rows || []).map((r) =>
                r.id === rowId ? { ...r, text } : r
              ),
            }
          : f
      ),
    });
  };

  const removeRow = (floorId, rowId) => {
    updateData({
      ...data,
      floors: (data.floors || []).map((f) =>
        f.id === floorId
          ? { ...f, rows: (f.rows || []).filter((r) => r.id !== rowId) }
          : f
      ),
    });
  };

  const toggleFloor = (floorId) => {
    setExpandedFloors((prev) => ({
      ...prev,
      [floorId]: !(prev[floorId] === true),
    }));
  };

  return (
    <div className="de-directory-register-panel">
      <div className="de-dr-section de-dr-section-property">
        <label className="de-dr-label">PROPERTY NAME</label>
        <input
          type="text"
          className="de-dr-input de-dr-input-property"
          placeholder="e.g. Östra Mårtensgatan 19"
          value={data.propertyName || ""}
          onChange={(e) => setPropertyName(e.target.value)}
        />
      </div>

      <div className="de-dr-section">
        <label className="de-dr-label" htmlFor="de-dr-column-count">
          Columns
        </label>
        <select
          id="de-dr-column-count"
          className="de-dr-input de-dr-column-select"
          value={columnSelectValue}
          onChange={(e) => setColumnCountMode(e.target.value)}
        >
          <option value="auto">Auto (by number of floors)</option>
          <option value="1">1 column</option>
          <option value="2">2 columns</option>
          <option value="3">3 columns</option>
          <option value="4">4 columns</option>
        </select>
        <p className="de-dr-hint">
          Choose a fixed count so the register matches your layout, or Auto to
          scale columns with floor count (legacy behavior).
        </p>
      </div>

      <div className="de-dr-section">
        <div className="de-dr-floors-header">
          <span className="de-dr-label">Floors & Rows</span>
        </div>
        <div className="de-dr-floors-list" ref={floorsListRef}>
          {(data.floors || []).map((floor) => {
            const isExpanded = expandedFloors[floor.id] === true;
            const rowCount = (floor.rows || []).length;
            return (
              <div
                key={floor.id}
                className="de-dr-floor-block"
                data-floor-id={floor.id}
              >
                <div
                  className="de-dr-floor-header"
                  onClick={() => toggleFloor(floor.id)}
                >
                  <span className="de-dr-floor-chevron">
                    {isExpanded ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </span>
                  <GripVertical size={14} className="de-dr-grip" />
                  <input
                    type="text"
                    className="de-dr-floor-name"
                    value={floor.name || ""}
                    onChange={(e) =>
                      updateFloor(floor.id, { name: e.target.value })
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="de-dr-floor-count">{rowCount}</span>
                  <button
                    type="button"
                    className="de-dr-floor-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFloor(floor.id);
                    }}
                    title="Delete floor"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
                {isExpanded && (
                  <div className="de-dr-rows">
                    <div className="de-dr-rows-scroll">
                      {(floor.rows || []).map((row) => (
                        <div key={row.id} className="de-dr-row">
                          <input
                            type="text"
                            className="de-dr-row-input"
                            placeholder="Name or label"
                            value={row.text || ""}
                            onChange={(e) =>
                              updateRow(floor.id, row.id, e.target.value)
                            }
                          />
                          <button
                            type="button"
                            className="de-dr-row-remove"
                            onClick={() => removeRow(floor.id, row.id)}
                            title="Remove row"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="de-dr-add-row"
                      onClick={() => addRow(floor.id)}
                    >
                      <Plus size={14} /> Add row
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="de-dr-add-floor-wrap">
          <input
            type="text"
            className="de-dr-input"
            placeholder="Floor name (required)"
            value={newFloorName}
            onChange={(e) => {
              setNewFloorName(e.target.value);
              if (floorNameError) setFloorNameError("");
            }}
          />
          <button
            type="button"
            className="de-dr-add-floor"
            onClick={addFloor}
          >
            <Plus size={14} /> Add floor
          </button>
        </div>
        {floorNameError && <p className="de-dr-error">{floorNameError}</p>}
      </div>

      {!isEditing && onAddToCanvas && (
        <button
          type="button"
          className="de-dr-add-to-canvas"
          onClick={() => onAddToCanvas(data)}
        >
          Add Register to canvas
        </button>
      )}
    </div>
  );
}

export default DirectoryRegister;
export { defaultRegisterData };
